import { RefObject, useEffect, useLayoutEffect, useRef } from 'react';

import { FILE_REFERENCE_CHIP_CLASS } from '@/components/chat/FileReferenceChip';

const MIRRORED_STYLES = [
    'fontFamily',
    'fontSize',
    'fontWeight',
    'fontStyle',
    'lineHeight',
    'letterSpacing',
    'wordSpacing',
    'textIndent',
    'tabSize',
    'paddingTop',
    'paddingRight',
    'paddingBottom',
    'paddingLeft'
] as const;

const escapeRegExp = (text: string) =>
    text.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

const referencePattern = (filenames: string[]) =>
    filenames.length === 0
        ? null
        : new RegExp(
              `(^|\\s)(#(?:${[...filenames]
                  .sort((a, b) => b.length - a.length)
                  .map(escapeRegExp)
                  .join('|')}))(?=\\s|$)`,
              'g'
          );

export const hasFileReference = (value: string, filenames: string[]) => {
    const pattern = referencePattern(filenames);
    return pattern !== null && pattern.test(value);
};

interface Props {
    value: string;
    filenames: string[];
    textareaRef: RefObject<HTMLTextAreaElement>;
}

const FileReferenceHighlight = ({ value, filenames, textareaRef }: Props) => {
    const mirrorRef = useRef<HTMLDivElement>(null);

    useLayoutEffect(() => {
        const textarea = textareaRef.current;
        const mirror = mirrorRef.current;
        if (!textarea || !mirror) return;
        const computed = window.getComputedStyle(textarea);
        MIRRORED_STYLES.forEach((property) => {
            mirror.style[property] = computed[property];
        });
        mirror.style.width = `${textarea.clientWidth}px`;
        mirror.style.height = `${textarea.clientHeight}px`;
        mirror.scrollTop = textarea.scrollTop;
    });

    useEffect(() => {
        const textarea = textareaRef.current;
        if (!textarea) return;
        const syncScroll = () => {
            if (mirrorRef.current) {
                mirrorRef.current.scrollTop = textarea.scrollTop;
            }
        };
        textarea.addEventListener('scroll', syncScroll);
        return () => textarea.removeEventListener('scroll', syncScroll);
    }, [textareaRef]);

    const pattern = referencePattern(filenames);
    const parts: React.ReactNode[] = [];
    let lastIndex = 0;
    if (pattern) {
        for (const match of value.matchAll(pattern)) {
            const start = match.index! + match[1].length;
            parts.push(value.slice(lastIndex, start));
            parts.push(
                <span
                    key={start}
                    data-file-reference={match[2].slice(1)}
                    className={FILE_REFERENCE_CHIP_CLASS}
                >
                    {match[2]}
                </span>
            );
            lastIndex = start + match[2].length;
        }
    }
    parts.push(value.slice(lastIndex));

    return (
        <div
            ref={mirrorRef}
            aria-hidden
            className="top-0 left-0 absolute overflow-hidden text-foreground wrap-break-word whitespace-pre-wrap pointer-events-none"
        >
            {parts}
            {'​'}
        </div>
    );
};

export default FileReferenceHighlight;
